export const AUTOMATED_FORM_FOLLOW_UP_CALL_KEY = 'automated_form_follow_up_call' as const;
export const INTELLIGENT_FORM_TRIAGE_KEY = 'intelligent_form_triage' as const;
export * from './manual-record-email.js';
export * from './call-triage.js';
export * from './contact-intelligence.js';
export * from './intake-lead-generation.js';
export * from './stripe-payments-sync.js';

export type SwitchDefinitionStatus = 'available' | 'coming_soon' | 'retired';
export type SwitchOperationalStatus =
  | 'inactive'
  | 'active'
  | 'paused'
  | 'error'
  | 'disabled'
  | 'coming_soon';
export type SwitchConfigurationStatus = 'not_started' | 'incomplete' | 'ready' | 'invalid';
export type SwitchDisplayStatus =
  | 'active'
  | 'paused'
  | 'setup_required'
  | 'error'
  | 'inactive'
  | 'coming_soon';

export interface QualificationQuestion {
  id: string;
  label: string;
  responseType: 'text' | 'yes_no' | 'single_select' | 'number';
  required: boolean;
  options: string[];
  leadFieldMapping: string;
}

export interface AvailabilityWindow {
  id: string;
  startAt: string;
  endAt: string;
  timezone: string;
  meetingDurationMinutes: number;
  assignedPortalUserId: string;
}

export interface FormFieldMapping {
  sourceField: string;
  targetField: string;
}

export interface AutomatedFormFollowUpCallSettings {
  general: {
    workflowName: string;
    internalDescription: string;
    timezone: string;
    assignedPortalUserId: string;
    assignedPortalUserName: string;
    assignedPortalUserEmail: string;
  };
  trigger: {
    formSource: string;
    formName: string;
    formIdentifier: string;
    triggerDelayMinutes: number;
    eligibleSubmissionTypes: string[];
  };
  calling: {
    callImmediately: boolean;
    delayBeforeCallMinutes: number;
    maximumAttempts: number;
    retryDelayMinutes: number;
    allowedStartTime: string;
    allowedEndTime: string;
    doNotCallOutsideBusinessHours: boolean;
  };
  receptionist: {
    receptionistId: string;
    receptionistName: string;
    openingMessage: string;
    objectives: string[];
    qualificationQuestions: QualificationQuestion[];
    closingBehavior: string;
  };
  scheduling: {
    availabilityWindows: AvailabilityWindow[];
    noAvailabilityBehavior: 'collect_preferences';
  };
  notifications: {
    callCompleted: boolean;
    leadQualified: boolean;
    appointmentScheduled: boolean;
    callFailed: boolean;
    email: string;
  };
  fieldMappings: FormFieldMapping[];
}

export interface SwitchConfigurationIssue {
  code: string;
  section:
    | 'general'
    | 'trigger'
    | 'calling'
    | 'receptionist'
    | 'scheduling'
    | 'notifications'
    | 'field_mapping'
    | 'triage'
    | 'platform';
  message: string;
}

export interface SwitchDefinition {
  id: string;
  key: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  version: number;
  status: SwitchDefinitionStatus;
  supportsUsageTracking: boolean;
}

export interface SwitchUsageSummary {
  period: 'current_month';
  executions: number;
  completed: number;
  answered: number;
  failed: number;
  qualified: number;
  appointmentsScheduled: number;
  summaryUpdates?: number;
  /**
   * Intelligent Form Triage counts, derived from the Form Entry records
   * themselves rather than a usage-event feed — the entries already carry the
   * classification and the timestamp the current-month window needs.
   */
  formTriage?: {
    analyzed: number;
    likelyServiceRequests: number;
    lowValueSeparated: number;
  };
  estimatedUsage: {
    minutes: number;
    calls: number;
    estimatedCost: number;
  };
  lastActivityAt: string | null;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
}

export interface PortalSwitchSummary {
  id: string;
  portalId: string;
  definition: SwitchDefinition;
  enabled: boolean;
  operationalStatus: SwitchOperationalStatus;
  configurationStatus: SwitchConfigurationStatus;
  displayStatus: SwitchDisplayStatus;
  configurationIssues: SwitchConfigurationIssue[];
  settings: AutomatedFormFollowUpCallSettings | import('./call-triage.js').IncomingCallTriageSettings | import('./call-triage.js').AnalyzeEndOfCallSettings | import('./contact-intelligence.js').ContactIntelligenceSettings | import('./intake-lead-generation.js').IntakeLeadGenerationSettings | import('./manual-record-email.js').ManualRecordEmailSettings;
  stateChangedBy: string | null;
  activatedAt: string | null;
  pausedAt: string | null;
  lastActivityAt: string | null;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
  lastErrorMessage: string | null;
  usage: SwitchUsageSummary;
}

export interface PortalUserOption {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface SwitchboardResponse {
  items: PortalSwitchSummary[];
  users: PortalUserOption[];
  canManage: boolean;
  executionReady: boolean;
}

export const DEFAULT_CALL_OBJECTIVES = [
  'Verify the lead submitted the request',
  'Confirm their contact information',
  'Confirm the requested service',
  'Ask the configured qualification questions',
  'Attempt to schedule a meeting when approved availability has been supplied',
  'Otherwise notify the assigned user that follow-up is needed',
];

export const DEFAULT_AUTOMATED_FORM_FOLLOW_UP_CALL_SETTINGS: AutomatedFormFollowUpCallSettings = {
  general: {
    workflowName: 'Automated Form Follow-Up Call',
    internalDescription: 'Verify and qualify new website leads before handing them to the team.',
    timezone: 'America/Los_Angeles',
    assignedPortalUserId: '',
    assignedPortalUserName: '',
    assignedPortalUserEmail: '',
  },
  trigger: {
    formSource: '',
    formName: '',
    formIdentifier: '',
    triggerDelayMinutes: 2,
    eligibleSubmissionTypes: [],
  },
  calling: {
    callImmediately: false,
    delayBeforeCallMinutes: 2,
    maximumAttempts: 2,
    retryDelayMinutes: 240,
    allowedStartTime: '09:00',
    allowedEndTime: '17:00',
    doNotCallOutsideBusinessHours: true,
  },
  receptionist: {
    receptionistId: '',
    receptionistName: '',
    openingMessage:
      'Hi, this is the Cascade Electric team following up on the request you submitted online.',
    objectives: [...DEFAULT_CALL_OBJECTIVES],
    qualificationQuestions: [],
    closingBehavior:
      'Confirm the next step. If no approved time is available, collect preferred days and times for a team follow-up.',
  },
  scheduling: {
    availabilityWindows: [],
    noAvailabilityBehavior: 'collect_preferences',
  },
  notifications: {
    callCompleted: true,
    leadQualified: true,
    appointmentScheduled: true,
    callFailed: true,
    email: '',
  },
  fieldMappings: [
    { sourceField: 'Name', targetField: 'lead.name' },
    { sourceField: 'Email', targetField: 'lead.email' },
    { sourceField: 'Phone', targetField: 'lead.phone' },
    { sourceField: 'Message', targetField: 'lead.message' },
    { sourceField: 'Service', targetField: 'lead.service' },
  ],
};

export interface SwitchExecutionCapabilities {
  triggerIntegrationReady?: boolean;
  callProviderReady?: boolean;
}

export function validateAutomatedFormFollowUpSettings(
  settings: AutomatedFormFollowUpCallSettings,
  capabilities: SwitchExecutionCapabilities = {},
): SwitchConfigurationIssue[] {
  const issues: SwitchConfigurationIssue[] = [];
  const add = (issue: SwitchConfigurationIssue) => issues.push(issue);

  if (!settings.general.assignedPortalUserId.trim()) {
    add({
      code: 'assigned_user_required',
      section: 'general',
      message: 'Choose the portal user who will receive lead handoffs.',
    });
  }
  if (!settings.general.timezone.trim()) {
    add({ code: 'timezone_required', section: 'general', message: 'Choose a workflow timezone.' });
  }
  if (!settings.trigger.formSource.trim() || !settings.trigger.formIdentifier.trim()) {
    add({
      code: 'form_trigger_required',
      section: 'trigger',
      message: 'Connect a form source and identifier before activating.',
    });
  }
  if (!settings.receptionist.receptionistId.trim()) {
    add({
      code: 'receptionist_required',
      section: 'receptionist',
      message: 'Choose the receptionist or assistant that will place the call.',
    });
  }
  if (
    !settings.receptionist.openingMessage.trim() ||
    settings.receptionist.objectives.length === 0
  ) {
    add({
      code: 'receptionist_behavior_required',
      section: 'receptionist',
      message: 'Add an opening message and at least one call objective.',
    });
  }

  const completeMappings = settings.fieldMappings.filter(
    (mapping) => mapping.sourceField.trim() && mapping.targetField.trim(),
  );
  if (!completeMappings.some((mapping) => mapping.targetField === 'lead.phone')) {
    add({
      code: 'lead_phone_mapping_required',
      section: 'field_mapping',
      message: 'Map the incoming lead phone number.',
    });
  }
  if (
    !completeMappings.some((mapping) =>
      ['lead.name', 'lead.first_name', 'lead.last_name', 'lead.fallback_identifier'].includes(
        mapping.targetField,
      ),
    )
  ) {
    add({
      code: 'lead_identifier_mapping_required',
      section: 'field_mapping',
      message: 'Map a lead name or fallback identifier.',
    });
  }
  if (settings.calling.maximumAttempts < 1 || settings.calling.maximumAttempts > 3) {
    add({
      code: 'maximum_attempts_invalid',
      section: 'calling',
      message: 'Maximum call attempts must be between 1 and 3.',
    });
  }
  if (settings.trigger.triggerDelayMinutes < 0 || settings.calling.delayBeforeCallMinutes < 0) {
    add({
      code: 'call_delay_invalid',
      section: 'calling',
      message: 'Call delays cannot be negative.',
    });
  }
  if (settings.calling.retryDelayMinutes < 60) {
    add({
      code: 'retry_delay_invalid',
      section: 'calling',
      message: 'Leave at least 60 minutes between call attempts.',
    });
  }
  if (!settings.calling.allowedStartTime || !settings.calling.allowedEndTime) {
    add({
      code: 'calling_hours_required',
      section: 'calling',
      message: 'Choose the allowed calling hours.',
    });
  }
  if (settings.scheduling.availabilityWindows.length > 3) {
    add({
      code: 'availability_limit',
      section: 'scheduling',
      message: 'Provide no more than three availability windows.',
    });
  }
  for (const window of settings.scheduling.availabilityWindows) {
    const start = Date.parse(window.startAt);
    const end = Date.parse(window.endAt);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      add({
        code: `availability_invalid:${window.id}`,
        section: 'scheduling',
        message: 'Each availability window needs a valid start and end time.',
      });
    }
    if (
      !window.timezone.trim() ||
      !window.assignedPortalUserId.trim() ||
      window.meetingDurationMinutes < 15
    ) {
      add({
        code: `availability_details_required:${window.id}`,
        section: 'scheduling',
        message: 'Each availability window needs a timezone, assigned user, and duration.',
      });
    }
  }
  for (const question of settings.receptionist.qualificationQuestions) {
    if (!question.label.trim()) {
      add({
        code: `question_label_required:${question.id}`,
        section: 'receptionist',
        message: 'Every qualification question needs a label.',
      });
    }
    if (question.responseType === 'single_select' && question.options.length === 0) {
      add({
        code: `question_options_required:${question.id}`,
        section: 'receptionist',
        message: 'Single-select questions need at least one option.',
      });
    }
  }
  const notificationsEnabled = Object.entries(settings.notifications).some(
    ([key, value]) => key !== 'email' && value === true,
  );
  if (
    notificationsEnabled &&
    !settings.notifications.email.trim() &&
    !settings.general.assignedPortalUserEmail.trim()
  ) {
    add({
      code: 'notification_email_required',
      section: 'notifications',
      message: 'Add a notification email or choose a portal user with an email.',
    });
  }
  if (capabilities.triggerIntegrationReady === false) {
    add({
      code: 'trigger_integration_unavailable',
      section: 'platform',
      message: 'The form trigger connection is not enabled for this install yet.',
    });
  }
  if (capabilities.callProviderReady === false) {
    add({
      code: 'call_provider_unavailable',
      section: 'platform',
      message: 'The calling provider is not enabled for this install yet.',
    });
  }

  return issues;
}

export function configurationStatusFromIssues(
  issues: SwitchConfigurationIssue[],
): SwitchConfigurationStatus {
  return issues.length === 0 ? 'ready' : 'incomplete';
}

export function deriveSwitchDisplayStatus(input: {
  enabled: boolean;
  operationalStatus: SwitchOperationalStatus;
  configurationStatus: SwitchConfigurationStatus;
}): SwitchDisplayStatus {
  if (input.operationalStatus === 'coming_soon' || input.operationalStatus === 'disabled') {
    return 'coming_soon';
  }
  if (input.operationalStatus === 'error') return 'error';
  if (input.configurationStatus !== 'ready') return 'setup_required';
  if (input.enabled && input.operationalStatus === 'active') return 'active';
  if (input.operationalStatus === 'paused') return 'paused';
  return 'inactive';
}

export const SWITCH_DISPLAY_STATUS_LABELS: Record<SwitchDisplayStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  setup_required: 'Setup required',
  error: 'Error',
  inactive: 'Inactive',
  coming_soon: 'Coming soon',
};
